import React, { useMemo, useState } from 'react';
import { CarIcon, PlusIcon } from 'lucide-react';
import { useAppState } from '../contexts/AppStateContext';
import { DEMO_DATE } from '../data/demo';
import { copy } from '../data/copy';
import { eventTypeLabels, sourceLabels } from '../data/labels';
import { namesOf } from '../utils/members';
import { formatDayLabel } from '../utils/time';
import { PageHeader } from '../components/ui/PageHeader';
import { Chip, Segmented } from '../components/ui/Form';
import { Button } from '../components/ui/Button';
import { EmptyNote } from '../components/ui/Panel';
import { EventSheet } from '../components/calendar/EventSheet';
import { AddEventSheet } from '../components/calendar/AddEventSheet';
import { AddSchoolSheet } from '../components/calendar/AddSchoolSheet';
import { SchoolList } from '../components/calendar/SchoolList';
type Mode = 'jaz' | 'vsi' | 'izbor';
export function CalendarPage() {
  const {
    family,
    viewer,
    activeMembers,
    memberById,
    isAdmin
  } = useAppState();
  const [seg, setSeg] = useState<'dogodki' | 'sola'>('dogodki');
  const [mode, setMode] = useState<Mode>('vsi');
  const [picked, setPicked] = useState<string[]>([]);
  const [openEvent, setOpenEvent] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addingSchool, setAddingSchool] = useState(false);
  const filterIds = mode === 'jaz' ? [viewer.id] : mode === 'vsi' ? activeMembers.map(m => m.id) : picked;
  const days = useMemo(() => {
    const visible = family.events.filter(e => e.participantIds.length === 0 || e.participantIds.some(p => filterIds.includes(p)) || e.driverId && filterIds.includes(e.driverId));
    const map = new Map<string, typeof visible>();
    visible.slice().sort((a, b) => (a.date + a.start).localeCompare(b.date + b.start)).forEach(e => map.set(e.date, [...(map.get(e.date) ?? []), e]));
    return Array.from(map.entries());
  }, [family.events, filterIds]);
  const toggle = (id: string) => {
    setMode('izbor');
    setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  };
  const connected = family.calendars.filter(c => c.status === 'vzorec');
  return <div data-id="element-1413" data-mp="ksr4kkloywn1">
      <PageHeader title="Koledar" subtitle={connected.length ? `Viri: App + ${connected.map(c => c.name).join(', ')} (vzorčni dogodki, Google še ni prebran)` : 'Vir: dogodki v aplikaciji'} action={<Button onClick={() => seg === 'dogodki' ? setAdding(true) : setAddingSchool(true)} data-id="element-1415" data-mp="axqs5jcfeuef">
            <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1416" data-mp="62tajo12zcpx" /> {seg === 'dogodki' ? 'Dogodek' : 'Šolsko'}
          </Button>} data-id="element-1414" data-mp="ksirospkjrbu" />
      <div className="flex flex-wrap items-center gap-3" data-id="element-1417" data-mp="3ta5cwhaydcy9">
        <Segmented label="Pogled koledarja" value={seg} onChange={setSeg} options={[{
        value: 'dogodki',
        label: 'Dogodki'
      }, {
        value: 'sola',
        label: 'Šola'
      }]} data-id="element-1418" data-mp="3t7iwrt9zxp4f" />
      </div>
      <div className="-mx-4 mt-4 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:px-0" role="group" aria-label="Filter po članih" data-id="element-1419" data-mp="396hbidgl4uw9">
        <Chip active={mode === 'jaz'} onClick={() => setMode('jaz')} data-id="element-1420" data-mp="3e8opf8kz6l18">
          Jaz
        </Chip>
        <Chip active={mode === 'vsi'} onClick={() => setMode('vsi')} data-id="element-1421" data-mp="3j3m2qrpafgjx">
          Vsi
        </Chip>
        {activeMembers.filter(m => m.id !== viewer.id).map(m => <Chip key={m.id} color={m.color} active={mode === 'izbor' && picked.includes(m.id)} onClick={() => toggle(m.id)} data-id="element-1422" data-mp="2qewwke9dkl1b">
              {m.name}
            </Chip>)}
      </div>

      <div className="mt-6" data-id="element-1423" data-mp="30dw6s19sgoqf">
        {seg === 'sola' ? <div className="max-w-3xl" data-id="element-1424" data-mp="3565poxchkoj7">
            <SchoolList memberIds={isAdmin ? filterIds : [viewer.id]} data-id="element-1425" data-mp="3578ijatdd11r" />
          </div> : days.length === 0 ? <EmptyNote data-id="element-1426" data-mp="27op54tf1fj0y">{copy.emptyEvents}</EmptyNote> : <div className="grid gap-6 lg:grid-cols-2" data-id="element-1427" data-mp="2co1vho54b88a">
            {days.map(([date, events]) => <section key={date} aria-labelledby={`d-${date}`} data-id="element-1428" data-mp="i9xnpdsmpy8g">
                <h2 id={`d-${date}`} className={`mb-2 px-1 font-display text-lg font-semibold capitalize ${date === DEMO_DATE ? 'text-accent-ink' : ''}`} data-id="element-1429" data-mp="db2z1v7zdfgf">
                  {date === DEMO_DATE ? 'Danes · ' : ''}
                  {formatDayLabel(date)}
                </h2>
                <ul className="divide-y divide-line rounded-3xl border-[1.5px] border-line bg-surface" data-id="element-1430" data-mp="10zw0263gmdhw">
                  {events.map(e => {
              const first = memberById(e.participantIds[0]);
              return <li key={e.id} data-id="element-1431" data-mp="1awqw8ic9g1t0">
                        <button type="button" onClick={() => setOpenEvent(e.id)} className="flex w-full gap-3 px-4 py-3 text-left transition-colors duration-150 hover:bg-canvas" data-id="element-1432" data-mp="1avpha2s4iv60">
                          <span className="w-1 shrink-0 self-stretch rounded-full" style={{
                    backgroundColor: e.participantIds.length === 1 && first ? first.color : '#F4B740'
                  }} aria-hidden="true" data-id="element-1433" data-mp="3cporslxf98hn" />
                          <span className="w-12 shrink-0 font-extrabold tabular-nums" data-id="element-1434" data-mp="1n92z6jpjn9y9">{e.start}</span>
                          <span className="min-w-0 flex-1" data-id="element-1435" data-mp="1s6mbvf6lfeo3">
                            <span className="block font-bold leading-snug" data-id="element-1436" data-mp="1dj9m1e9r6mrw">{e.title}</span>
                            <span className="block text-xs text-muted" data-id="element-1437" data-mp="1igt30lxu5dnj">
                              {e.participantIds.length ? namesOf(family, e.participantIds) : 'Vsi'} · {eventTypeLabels[e.type]} · {sourceLabels[e.source]}
                            </span>
                            {(e.driverId || e.needsDriver) && <span className={`mt-0.5 inline-flex items-center gap-1 text-xs font-semibold ${e.driverId ? 'text-info-ink' : 'text-review-ink'}`} data-id="element-1438" data-mp="ul6uarptmbqe">
                                <CarIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1439" data-mp="2ox5691fillpz" />
                                {e.driverId ? `Pelje ${memberById(e.driverId)?.name}` : 'Kdo pelje? Še nihče'}
                                {e.pickupId ? ` · iskat ${memberById(e.pickupId)?.name}` : ''}
                              </span>}
                          </span>
                        </button>
                      </li>;
            })}
                </ul>
              </section>)}
          </div>}
      </div>
      <p className="mt-6 text-xs text-muted" data-id="element-1440" data-mp="3b62dtn0wo2yj">
        {family.rules.visibility.calendarAll ? 'Vsi člani vidijo vse dogodke in filtrirajo po osebah. ' : ''}Izvoz nazaj v Google je poznejša razširitev.
      </p>

      <EventSheet eventId={openEvent} onClose={() => setOpenEvent(null)} data-id="element-1441" data-mp="31g0o6qswmchi" />
      <AddEventSheet open={adding} onClose={() => setAdding(false)} data-id="element-1442" data-mp="31dwettc8kl9u" />
      <AddSchoolSheet open={addingSchool} onClose={() => setAddingSchool(false)} data-id="element-1443" data-mp="2xe5qdwa6mh8h" />
    </div>;
}